import { Link } from "react-router-dom";

// Auto-linking utility: scans story text for known entity / phenomenon mentions
// and turns them into clickable Wikipedia-style internal links.
//
// Usage: <AutoLinkedText text={enc.what_happened} />

const ENTITY_LINKS = [
    { match: /\b(Tall Grey|Tall Greys)\b/g, to: "/species/tall-grey" },
    { match: /\b(Greys?)\b/g, to: "/species/grey" },
    { match: /\b(Mantis|Mantid|Mantids)\b/g, to: "/species/mantid" },
    { match: /\b(Reptilian|Reptilians)\b/g, to: "/species/reptilian" },
    { match: /\b(Nordic|Nordics)\b/g, to: "/species/nordic" },
    { match: /\b(Shadow Being|Shadow People|Shadow Person|Shadow Figure)\b/g, to: "/species/shadow-being" },
    { match: /\b(Orb|Orbs|Orb Intelligence)\b/g, to: "/species/orb-intelligence" },
    { match: /\b(Hybrid|Hybrids)\b/g, to: "/species/hybrid" },
];

const TAG_LINKS = [
    { match: /\b(missing time)\b/gi, tag: "missing-time" },
    { match: /\b(telepath\w*)\b/gi, tag: "telepathy" },
    { match: /\b(abduction|abducted)\b/gi, tag: "abduction" },
    { match: /\b(close encounter)\b/gi, tag: "close-encounter" },
    { match: /\b(mass sighting)\b/gi, tag: "mass-sighting" },
];

const UNIVERSE_LINKS = [
    { match: /\b(Zeta Reticuli)\b/g, to: "/universe" },
    { match: /\b(Pleiades|Pleiadian|Pleiadians)\b/g, to: "/universe" },
    { match: /\b(Orion belt|Orion)\b/g, to: "/universe" },
    { match: /\b(Sirius)\b/g, to: "/universe" },
    { match: /\b(Lyra|Lyran)\b/g, to: "/universe" },
    { match: /\b(Arcturus|Arcturian|Arcturians)\b/g, to: "/universe" },
    { match: /\b(Andromeda)\b/g, to: "/universe" },
    { match: /\b(Draco|Draconian)\b/g, to: "/universe" },
];

export function AutoLinkedText({ text }) {
    if (!text) return null;

    // Build a single pass: find all matches with their positions, then build segments.
    const matches = [];
    const allRules = [
        ...ENTITY_LINKS.map((r) => ({ ...r, kind: "entity" })),
        ...TAG_LINKS.map((r) => ({ ...r, kind: "tag" })),
        ...UNIVERSE_LINKS.map((r) => ({ ...r, kind: "universe" })),
    ];
    for (const rule of allRules) {
        const re = new RegExp(rule.match.source, rule.match.flags);
        let m;
        while ((m = re.exec(text)) !== null) {
            matches.push({ start: m.index, end: m.index + m[0].length, label: m[0], rule });
        }
    }
    matches.sort((a, b) => a.start - b.start);

    // De-overlap: prefer the first occurrence, skip later overlaps.
    const dedup = [];
    let cursor = 0;
    for (const m of matches) {
        if (m.start >= cursor) {
            dedup.push(m);
            cursor = m.end;
        }
    }

    if (dedup.length === 0) return <>{text}</>;

    const parts = [];
    let pos = 0;
    dedup.forEach((m, i) => {
        if (m.start > pos) parts.push(<span key={`t-${i}`}>{text.slice(pos, m.start)}</span>);
        const to = m.rule.kind === "tag"
            ? `/encounters?tag=${m.rule.tag}`
            : m.rule.to;
        parts.push(
            <Link key={`l-${i}`} to={to} className="font-medium underline underline-offset-2 hover:text-[var(--ok-accent)]" style={{ textDecorationColor: "var(--ok-accent)" }} data-testid={`autolink-${m.rule.kind}-${i}`}>
                {m.label}
            </Link>
        );
        pos = m.end;
    });
    if (pos < text.length) parts.push(<span key="tail">{text.slice(pos)}</span>);
    return <>{parts}</>;
}
